/** Shared formatting helpers for live fleet data */

export const dash = '—'

export const formatFleetValue = (value, decimals = 1) => {
  if (value == null || value === '' || Number.isNaN(Number(value))) return dash
  const num = Number(value)
  if (decimals === 0) return Math.round(num).toLocaleString('en-US')
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

export const isFracFleet = (fleetType) =>
  String(fleetType || '').toLowerCase() === 'fracfleet'

/** Extract unit array from SignalR payload (array or wrapped object). */
export const extractFleetUnits = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  if (Array.isArray(payload.units)) return payload.units
  if (Array.isArray(payload.Units)) return payload.Units
  if (Array.isArray(payload.data?.units)) return payload.data.units
  if (Array.isArray(payload.data)) return payload.data
  return []
}

const CATEGORY_TO_DETAIL_KEY = {
  engine: 'engine',
  transmission: 'transmission',
  pump: 'pump',
  hydraulic: 'hydraulic',
  dgb: 'dgb',
}

/** Map API fieldName → UI key used by tiles / settings / detail modal. */
const DETAIL_FIELD_ALIASES = {
  engine: {
    EngineRPM: 'engineRPM',
    ECMHours: 'ecmHours',
    EngineIdleHours: 'engineIdleHours',
    EngineITR: 'engineITR',
    EngineCoolantTemp: 'coolantTemp',
    FuelRate: 'fuelRate',
  },
  transmission: {
    TransPressure: 'pressure',
    TransTemperature: 'temperature',
    TorqueConvTemp: 'torqueConvTemp',
    InputSpeed: 'inputSpeed',
    OutputSpeed: 'outputSpeed',
    HrsRunning: 'hrsRunning',
    HrsInGear: 'hrsInGear',
    CurrentGear: 'currentGear',
  },
  pump: {
    LubeOilPressure: 'lubeOilPressure',
    LubeOilTemp: 'lubeOilTemp',
    PumpSpeed: 'pumpSpeed',
    WorkhoursPowerEnd: 'workhoursPowerEnd',
    WorkhoursFluidEnd: 'workhoursFluidEnd',
    DischargePressure: 'dischargePressure',
    RodLoad: 'rodLoad',
  },
  hydraulic: {
    FanSpeed: 'fanSpeed',
    CPumpSpeed: 'cPumpSpeed',
    // New spaced response uses "Pump Speed" for C-Pump speed
    PumpSpeed: 'cPumpSpeed',
    ChargePressure: 'chargePressure',
    WorkPressure: 'workPressure',
    HydraulicSetpoint: 'setpoint',
    HydraulicTemperature: 'temperature',
  },
  dgb: {
    DGBActualRate: 'actualRate',
    DGBRateSetpoint: 'rateSetpoint',
    EngineTorque: 'engineTorque',
    DGBState: 'state',
  },
}

const TABLE_FIELD_ALIASES = {
  Gear: 'gear',
  RPM: 'rpm',
  DischargePressure: 'dischargePressure',
  DischargeRate: 'dischargeRate',
  Fuel: 'fuel',
  Diesel: 'fuel',
  GasSub: 'gasSub',
  TransTemp: 'transTemp',
  CoolantTemp: 'coolantTemp',
  Suction: 'suction',
  RodLoad: 'rodLoad',
}

const BLENDER_FIELD_ALIASES = {
  TreatingPressure: 'treatingPressure',
  SlurryRate: 'slurryRate',
  SandConcentration: 'sandConcentration',
  ProppantStageTotal: 'proppantStageTotal',
  MixTubLevel: 'mixTubLevel',
}

/** "Engine RPM" / "EngineRPM" → "enginerpm" for alias matching. */
const normalizeAliasLookupKey = (name) =>
  String(name || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()

const buildAliasLookup = (aliases = {}) => {
  const lookup = Object.create(null)
  Object.entries(aliases).forEach(([from, to]) => {
    lookup[normalizeAliasLookupKey(from)] = to
  })
  return lookup
}

const DETAIL_ALIAS_LOOKUPS = Object.fromEntries(
  Object.entries(DETAIL_FIELD_ALIASES).map(([category, aliases]) => [
    category,
    buildAliasLookup(aliases),
  ]),
)
const TABLE_ALIAS_LOOKUP = buildAliasLookup(TABLE_FIELD_ALIASES)
const BLENDER_ALIAS_LOOKUP = buildAliasLookup(BLENDER_FIELD_ALIASES)

/** "Engine RPM" → "engineRpm"; "DischargePressure" → "dischargePressure". */
const toFieldKey = (name) => {
  if (!name || typeof name !== 'string') return ''
  const parts = name.trim().split(/[^a-zA-Z0-9]+/).filter(Boolean)
  if (!parts.length) return ''
  return parts
    .map((part, i) => {
      const lower = part.toLowerCase()
      if (i === 0) return lower
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join('')
}

const resolveFieldKey = (fieldName, aliasLookup = {}) => (
  aliasLookup[normalizeAliasLookupKey(fieldName)] || toFieldKey(fieldName)
)

const emptyDetailData = () => ({
  engine: {},
  transmission: {},
  pump: {},
  hydraulic: {},
  dgb: {},
})

/**
 * Normalize a raw unit from the new fields[] response into shapes used by UI:
 * pumpData / blenderData / pumpDetailData.
 * Legacy units that already have those objects are returned as-is.
 */
export const normalizeFleetUnit = (unit) => {
  if (!unit || typeof unit !== 'object') return unit

  const fields = Array.isArray(unit.fields) ? unit.fields : null
  if (!fields) return unit

  const pumpData = { ...(unit.pumpData || {}) }
  const blenderData = { ...(unit.blenderData || {}) }
  const pumpDetailData = {
    ...emptyDetailData(),
    ...(unit.pumpDetailData || {}),
  }
  Object.keys(CATEGORY_TO_DETAIL_KEY).forEach((key) => {
    pumpDetailData[key] = { ...(pumpDetailData[key] || {}) }
  })

  const sorted = [...fields].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))

  sorted.forEach((field) => {
    if (!field || !field.fieldName) return
    const category = String(field.category || '').toLowerCase()
    const value = field.value
    const fieldName = field.fieldName

    if (category === 'table') {
      const key = resolveFieldKey(fieldName, TABLE_ALIAS_LOOKUP)
      if (key) pumpData[key] = value
    } else if (category === 'blender') {
      const key = resolveFieldKey(fieldName, BLENDER_ALIAS_LOOKUP)
      if (key) blenderData[key] = value
    }

    const detailKey = CATEGORY_TO_DETAIL_KEY[category]
    if (detailKey && field.isDetailField) {
      const key = resolveFieldKey(fieldName, DETAIL_ALIAS_LOOKUPS[detailKey])
      if (key) pumpDetailData[detailKey][key] = value
    }
  })

  // Keep settings-compatible pump detail keys that now live under Table category
  if (pumpData.dischargePressure != null && pumpDetailData.pump.dischargePressure == null) {
    pumpDetailData.pump.dischargePressure = pumpData.dischargePressure
  }
  if (pumpData.rodLoad != null && pumpDetailData.pump.rodLoad == null) {
    pumpDetailData.pump.rodLoad = pumpData.rodLoad
  }

  const equipmentType = String(unit.equipmentType || '').toLowerCase()
  const hasPumpData = Object.keys(pumpData).length > 0
  const hasBlenderData = Object.keys(blenderData).length > 0

  return {
    ...unit,
    pumpData: (equipmentType === 'pump' || hasPumpData) ? pumpData : unit.pumpData,
    blenderData: (equipmentType === 'blender' || hasBlenderData) ? blenderData : unit.blenderData,
    pumpDetailData: equipmentType === 'pump' ? pumpDetailData : unit.pumpDetailData,
  }
}

export const normalizeFleetUnits = (payloadOrUnits) => {
  const units = Array.isArray(payloadOrUnits)
    ? payloadOrUnits
    : extractFleetUnits(payloadOrUnits)
  return units.map(normalizeFleetUnit)
}

/** Outside the subscription: listed greyed out, the server streams no data for it. Older payloads have no flag. */
export const isUnitBillable = (unit) => unit?.isBillable !== false

export const splitFleetUnits = (units = []) => {
  const pumps = []
  const blenders = []
  const other = []
  units.forEach((unit) => {
    const type = String(unit.equipmentType || '').toLowerCase()
    if (type === 'pump') pumps.push(unit)
    else if (type === 'blender') blenders.push(unit)
    else other.push(unit)
  })
  return { pumps, blenders, other }
}

export const aggregateBlenderKpis = (blenders = []) => {
  if (!blenders.length) {
    return [
      { name: 'Treating pressure', value: dash, unit: 'psi', color: '#3fc6e8' },
      { name: 'Slurry rate', value: dash, unit: 'bbl/min', color: '#5ea8ff' },
      { name: 'Sand concentration', value: dash, unit: 'PPA', color: '#7c8cf0' },
      { name: 'Proppant stage total', value: dash, unit: 'lbs', color: '#46d3c0' },
      { name: 'Mix tub level', value: dash, unit: '%', color: '#4a8ef0' },
    ]
  }

  // Prefer first blender with data; fallback to averages across available values
  const withData = blenders.filter((b) => b.blenderData)
  const source = withData[0]?.blenderData || {}
  const avg = (getter, decimals) => {
    const vals = withData.map(getter).filter((v) => v != null && !Number.isNaN(Number(v)))
    if (!vals.length) return dash
    const mean = vals.reduce((s, v) => s + Number(v), 0) / vals.length
    return formatFleetValue(mean, decimals)
  }

  return [
    {
      name: 'Treating pressure',
      value: source.treatingPressure != null
        ? formatFleetValue(source.treatingPressure, 0)
        : avg((b) => b.blenderData?.treatingPressure, 0),
      unit: 'psi',
      color: '#3fc6e8',
    },
    {
      name: 'Slurry rate',
      value: source.slurryRate != null
        ? formatFleetValue(source.slurryRate, 1)
        : avg((b) => b.blenderData?.slurryRate, 1),
      unit: 'bbl/min',
      color: '#5ea8ff',
    },
    {
      name: 'Sand concentration',
      value: source.sandConcentration != null
        ? formatFleetValue(source.sandConcentration, 1)
        : avg((b) => b.blenderData?.sandConcentration, 1),
      unit: 'PPA',
      color: '#7c8cf0',
    },
    {
      name: 'Proppant stage total',
      value: source.proppantStageTotal != null
        ? formatFleetValue(source.proppantStageTotal, 0)
        : avg((b) => b.blenderData?.proppantStageTotal, 0),
      unit: 'lbs',
      color: '#46d3c0',
    },
    {
      name: 'Mix tub level',
      value: source.mixTubLevel != null
        ? formatFleetValue(source.mixTubLevel, 1)
        : avg((b) => b.blenderData?.mixTubLevel, 1),
      unit: '%',
      color: '#4a8ef0',
    },
  ]
}

